import api from './api';

export interface CreateDealershipRequest {
  name: string;
  description: string;
  location: string;
}

export interface DealershipInfo {
  id: string;
  name: string;
  description: string;
  location: string;
  owner: {
    id: string;
    username: string;
  };
  money: number;
  reputation: number;
  level: number;
  inventory: Array<{
    carId: number;
    carName: string;
    carImage: string;
    quantity: number;
    purchasePrice: number;
    sellingPrice: number;
    lastRestocked: Date;
  }>;
  currentInventory: number;
  maxInventory: number;
  totalInventoryValue: number;
  totalPotentialProfit: number;
}

export interface DealershipStats {
  totalCarsSold: number;
  totalInventoryValue: number;
  totalPotentialProfit: number;
  reputation: number;
  level: number;
  currentInventory: number;
  maxInventory: number;
}

export interface RestockRequest {
  carId: number;
  quantity: number;
  purchasePrice: number;
}

export interface PurchaseCarRequest {
  carId: number;
  quantity: number;
}

export interface AvailableCar {
  id: number;
  name: string;
  value: number;
  speed: number;
  successRate: number;
  image: string;
  category: string;
  rankRequired?: number;
  price?: number; // For restocking
  purchasePrice?: number; // For purchasing (20% below base value)
  description: string;
}

class DealershipService {
  /**
   * Create a new dealership
   */
  async createDealership(data: CreateDealershipRequest): Promise<{ success: boolean; message: string; dealership?: any }> {
    try {
      const response = await api.post('/dealership/create', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create dealership'
      };
    }
  }

  /**
   * Get user's own dealership
   */
  async getMyDealership(): Promise<{ 
    success: boolean; 
    hasDealership: boolean; 
    dealership?: DealershipInfo; 
    stats?: DealershipStats;
    message?: string;
  }> {
    try {
      const response = await api.get('/dealership/my-dealership');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        hasDealership: false,
        message: error.response?.data?.message || 'Failed to get dealership'
      };
    }
  }

  /**
   * Get all dealerships in a location
   */
  async getDealershipsInLocation(location: string): Promise<{ 
    success: boolean; 
    dealerships: DealershipInfo[];
    message?: string;
  }> {
    try {
      const response = await api.get(`/dealership/location/${location}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        dealerships: [],
        message: error.response?.data?.message || 'Failed to get dealerships'
      };
    }
  }

  /**
   * Get specific dealership info
   */
  async getDealershipInfo(dealershipId: string): Promise<{ 
    success: boolean; 
    dealership: DealershipInfo;
    message?: string;
  }> {
    try {
      const response = await api.get(`/dealership/${dealershipId}/info`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get dealership info');
    }
  }

  /**
   * Restock dealership inventory
   */
  async restockInventory(dealershipId: string, data: RestockRequest): Promise<{ 
    success: boolean; 
    message: string; 
    result?: any;
  }> {
    try {
      const response = await api.post(`/dealership/${dealershipId}/restock`, data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to restock inventory'
      };
    }
  }

  /**
   * Purchase car from dealership
   */
  async purchaseCar(dealershipId: string, data: PurchaseCarRequest): Promise<{ 
    success: boolean; 
    message: string; 
    result?: any;
  }> {
    try {
      const response = await api.post(`/dealership/${dealershipId}/purchase`, data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to purchase car'
      };
    }
  }

  /**
   * Upgrade dealership
   */
  async upgradeDealership(dealershipId: string): Promise<{ 
    success: boolean; 
    message: string; 
    result?: any;
  }> {
    try {
      const response = await api.post(`/dealership/${dealershipId}/upgrade`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upgrade dealership'
      };
    }
  }

  /**
   * Get restock recommendations
   */
  async getRestockRecommendations(dealershipId: string): Promise<{ 
    success: boolean; 
    recommendations: any[];
    message?: string;
  }> {
    try {
      const response = await api.get(`/dealership/${dealershipId}/recommendations`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        recommendations: [],
        message: error.response?.data?.message || 'Failed to get recommendations'
      };
    }
  }

  /**
   * Get available cars for restocking
   */
  async getAvailableCarsForRestock(): Promise<{ 
    success: boolean; 
    cars: AvailableCar[];
    message?: string;
  }> {
    try {
      const response = await api.get('/dealership/available-cars');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        cars: [],
        message: error.response?.data?.message || 'Failed to get available cars'
      };
    }
  }

  /**
   * Get available cars for purchase from a specific dealership
   */
  async getAvailableCarsForPurchase(dealershipId: string): Promise<{ 
    success: boolean; 
    cars: AvailableCar[];
    message?: string;
  }> {
    try {
      const response = await api.get(`/dealership/${dealershipId}/available-cars`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        cars: [],
        message: error.response?.data?.message || 'Failed to get available cars'
      };
    }
  }

  /**
   * Purchase car directly from dealership (from cars array)
   */
  async purchaseCarFromDealership(dealershipId: string, carId: number, quantity: number = 1): Promise<{ 
    success: boolean; 
    message: string; 
    result?: any;
  }> {
    try {
      const response = await api.post(`/dealership/${dealershipId}/purchase`, { carId, quantity });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to purchase car'
      };
    }
  }
}

export const dealershipService = new DealershipService();
